/**
 * Alpine-Bootstrap Modal
 *
 * The aim of this library is to provide a simple way to create a Bootstrap modal, enhanced with Alpine.
 */

let modalCount = 0;
let firstModalPaddingRight = 0;
const falseToken = '[FALSE]';
const beginSegmentToken = '#BEGINSEGMENT';
const endSegmentToken = '#ENDSEGMENT';

function beginSegment(value) {
    return beginSegmentToken + (value ? '' : falseToken);
}

function endSegment() {
    return endSegmentToken;
}

function removeFalseSegments(value) {
    let startPos = value.indexOf(beginSegmentToken);
    let endPos;

    if (startPos !== -1) {
        endPos = value.indexOf(endSegmentToken);

        if (endPos === -1) return value;
    } else {
        return value;
    }

    let beginCount = value.substring(startPos + beginSegmentToken.length, endPos).match(new RegExp(beginSegmentToken, 'g'))?.length || 0;

    if (beginCount > 0) {
        for (let i = 0; i < beginCount; i++) {
            endPos = value.indexOf(endSegmentToken, endPos + endSegmentToken.length);
        }
    }

    let segment = value.substring(startPos, endPos + endSegmentToken.length);

    if (segment.startsWith(beginSegmentToken + falseToken)) {
        return removeFalseSegments(value.replace(segment, ''));
    }

    return removeFalseSegments(value.replace(segment, segment.slice(beginSegmentToken.length, -endSegmentToken.length)));
}

function nextDoubleTicks(callback) {
    Alpine.nextTick(() => Alpine.nextTick(() => callback()));
}

const defaultConfig = {
    accentWidth: '5px',
    animate: false,
    backdrop: false,
    backdropBlur: true,
    centered: false,
    closeButton: false,
    focus: true,
    onEscape: false,
    scrollable: false,
    size: '',
};

function abmodal(config = {}) {
    const id = 'abmodal_' + Date.now();

    let modalContainer;
    let closeable = true;

    config = Object.assign(
        {},
        defaultConfig,
        {
            accent: '',
            buttons: {},
            data: {},
            id: id,
            isForm: false,
            message: '',
            onClose: null,
            onClosing: null,
            onFocus: null,
            onOpen: null,
            onOpening: null,
            title: '',
        },
        config
    );

    const data = {
        onOpening: config.onOpening,
        onOpen: config.onOpen,
        onFocus: config.onFocus,
        onClosing: config.onClosing,
        onClose: config.onClose,
    };

    Object.defineProperties(data, {
        modalId: {
            value: id,
        },
        isInModal: {
            value: true,
        },
        focus: {
            value: () => {
                modalContainer.focus();

                if (typeof data.onFocus === 'function') {
                    data.onFocus();
                }
            },
        },
        close: {
            value: () => {
                if (IS_BOOTSTRAP4) {
                    $(modalContainer).modal('hide');
                } else if (IS_BOOTSTRAP5) {
                    bootstrap.Modal.getInstance(modalContainer)?.hide();
                }

                return !document.contains(modalContainer);
            },
        },
        preventClose: {
            value: () => !(closeable = false),
        },
    });

    let xData = id;
    let configData = config.data;
    window[id] = data;

    if (typeof configData === 'string') {
        configData = configData.replace(/'/g, '&#039;').replace(/"/g, '&quot;');

        if (configData === configData.replace('(', '').replace(')', '')) {
            configData += '()';
        }

        xData = `Helper.mergeObjects(${id}, ${configData})`;
    } else {
        Helper.mergeObjects(data, configData || {});
    }

    const sizes = {
        'small': 'modal-sm',
        'sm': 'modal-sm',
        'large': 'modal-lg',
        'lg': 'modal-lg',
        'extra-large': 'modal-xl',
        'xl': 'modal-xl',
    };

    let message = config.message;
    let messageCallback = null;

    if (message instanceof Element) {
        message = message.innerHTML;
    } else if (message instanceof URL) {
        const url = message;

        message = async () => {
            try {
                const response = await axios.get(url, {
                    headers: {
                        'Accept': 'text/html',
                        'Content-Type': null,
                    },
                });

                return response.data;
            } catch (error) {
                console.error(error);

                return '';
            }
        };
    }

    if (typeof message === 'function') {
        messageCallback = message;
        message = '<div class="text-center"><div class="spinner-border text-secondary" style="font-size: 1.5rem; width: 4rem; height: 4rem"></div></div>';
    }

    let buttons = '';
    const noop = () => {};

    for (const [key, detail] of Object.entries(config.buttons)) {
        const buttonName = key + 'Button';
        let callback;

        if (typeof data[buttonName] === 'function') {
            callback = data[buttonName];
        } else {
            callback = detail.callback || noop;
        }

        data[buttonName] = async function (event) {
            const result = await callback.call(this, event);

            if (closeable) {
                closeable = typeof result === 'undefined' ? true : !!result;
            }

            this.close();
        };

        buttons += `<button type="${detail.type || 'button'}" class="btn ${detail.className || 'btn btn-light'}" x-ref="${buttonName}" x-on:click="${buttonName}" ${detail.disabled ? 'disabled' : ''}>${detail.label || ''}</button>`;
    }

    let closeButtonTemplate;

    if (IS_BOOTSTRAP4) {
        closeButtonTemplate = '<button type="button" class="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>';
    } else if (IS_BOOTSTRAP5) {
        closeButtonTemplate = '<button type="button" class="btn-close float-end" data-bs-dismiss="modal" aria-label="Close"></button>';
    }

    document.body.insertAdjacentHTML(
        'beforeend',
        removeFalseSegments(`<${config.isForm ? 'form method="POST"' : 'div'} x-data="${xData}" class="modal ${config.animate ? 'fade' : ''}" id="${id}" x-title="${config.id}" tabindex="-1">${beginSegment(config.isForm)}<input type="hidden" name="_method" value="POST">${endSegment()}<div class="modal-dialog ${config.scrollable ? 'modal-dialog-scrollable' : ''} ${config.centered ? 'modal-dialog-centered' : ''} ${sizes[config.size] || ''}"><div class="modal-content">${beginSegment(config.title)}<div class="modal-header" x-ref="modalHeader"><h5 class="modal-title">${config.title}</h5>${config.closeButton ? closeButtonTemplate : ''}</div>${endSegment()}<div class="modal-body" x-ref="modalBody" style="visibility: hidden">${!config.title && config.closeButton ? closeButtonTemplate : ''}${message}</div>${beginSegment(buttons)}<div class="modal-footer" x-ref="modalFooter">${buttons}</div>${endSegment()}</div></div></${config.isForm ? 'form' : 'div'}>`)
    );

    modalContainer = document.getElementById(id);

    const elementsInModal = {};
    modalContainer.querySelectorAll('[id]:not([type="checkbox"], [type="radio"])').forEach((element) => (elementsInModal[element.id] = element));

    modalContainer.querySelectorAll('label:not(.custom-control-label, .form-check-label)').forEach((label) => {
        const element = elementsInModal[label.getAttribute('for')] ?? null;

        if (!element) return;

        label.addEventListener('click', (event) => {
            event.preventDefault();

            const xData = Helper.getAlpineData(element);

            if (xData && !xData.modalId && xData.focus) {
                xData.focus();
            } else {
                element.focus();
            }
        });
    });

    const modalBody = modalContainer.querySelector('.modal-body');

    const modalOptions = {
        backdrop: config.backdrop ? 'static' : true,
        keyboard: config.onEscape,
        focus: false,
        show: false,
    };

    const modalEventHandlers = {
        show(event) {
            ['tooltip', 'popover'].forEach((type) => {
                if (IS_BOOTSTRAP4) {
                    $(`.${type}`)[type]('hide');
                } else if (IS_BOOTSTRAP5) {
                    document
                        .querySelectorAll(`[aria-describedby^="${type}"]`)
                        .forEach((element) => bootstrap[type.charAt(0).toUpperCase() + type.slice(1)].getInstance(element)?.hide());
                }
            });

            let show = true;

            if (typeof data.onOpening === 'function') {
                show = data.onOpening(event);
                show = typeof show === 'undefined' ? true : show;
            }

            if (show && config.animate) {
                nextDoubleTicks(() => (modalBody.style.visibility = 'visible'));
            }

            if (!show) {
                event.preventDefault();
                modalContainer.remove();
            }

            return show;
        },

        shown(event) {
            if (config.focus) {
                modalContainer.focus();
            }

            const triggerOnOpen = () => {
                if (typeof data.onOpen === 'function') {
                    data.onOpen(event);
                }

                if (typeof data.onFocus === 'function') {
                    data.onFocus();
                }
            };

            if (config.animate) {
                triggerOnOpen();
            } else {
                nextDoubleTicks(() => {
                    modalBody.style.visibility = 'visible';
                    triggerOnOpen();
                });
            }

            if (messageCallback) {
                (async () => {
                    const closeButton = !config.title && config.closeButton ? closeButtonTemplate : '';
                    const message = await messageCallback(modalBody, closeButton);

                    modalBody.innerHTML = closeButton + message;
                })();
            }

            modalContainer.style.zIndex = 1050 + 10 * modalCount;
            modalContainer.style.paddingRight = modalCount > 0 ? firstModalPaddingRight : modalContainer.style.paddingRight;
            modalContainer.nextElementSibling.style.zIndex = 1041 + 10 * modalCount;
            modalContainer.nextElementSibling.style.opacity = 0.5 - 0.05 * modalCount;

            if (config.accent) {
                const modalContent = modalContainer.querySelector('.modal-content');

                let [color, width = config.accentWidth] = config.accent.split(' ', 2);

                if (color) {
                    modalContent.style.borderTopColor = color;
                    modalContent.style.borderTopWidth = width;
                }
            }

            if (config.backdropBlur) {
                const radius = config.backdropBlur === true ? '0.85px' : config.backdropBlur;

                modalContainer.style.backdropFilter = `blur(${radius})`;
            }

            firstModalPaddingRight = modalCount === 0 ? modalContainer.style.paddingRight : firstModalPaddingRight;
            modalCount++;
        },

        hide(event) {
            if (closeable && typeof data.onClosing === 'function') {
                const result = data.onClosing(event);
                closeable = typeof result === 'undefined' ? true : !!result;
            }

            if (closeable) return;

            event.preventDefault();
            closeable = true;

            return false;
        },

        hidden(event) {
            if (IS_BOOTSTRAP4) {
                $(modalContainer).modal('dispose').remove();
            } else if (IS_BOOTSTRAP5) {
                bootstrap.Modal.getInstance(modalContainer)?.dispose();
                modalContainer.remove();
            }

            if (typeof data.onClose === 'function') {
                data.onClose(event);
            }

            delete window[id];
            modalCount--;

            if (modalCount > 0) {
                document.body.classList.add('modal-open');
                document.body.style.paddingRight = firstModalPaddingRight;

                const modals = document.querySelectorAll('.modal');
                modals[modals.length - 1]?.focus();
            }
        },
    };

    const registerModalEventHandlers = (registrar) => Object.entries(modalEventHandlers).forEach(([event, handler]) => registrar(`${event}.bs.modal`, handler));

    if (IS_BOOTSTRAP4) {
        const modal = $(modalContainer).modal(modalOptions);

        registerModalEventHandlers((...args) => modal.on(...args));
        modal.modal('show');
    } else if (IS_BOOTSTRAP5) {
        const modal = new bootstrap.Modal(modalContainer, modalOptions);

        registerModalEventHandlers((...args) => modalContainer.addEventListener(...args));
        modal.show();
    }

    return window[id];
}

Object.defineProperties(abmodal, {
    setDefaultConfig: {
        value: (config) => Object.assign(defaultConfig, config),
    },
    getDefaultConfig: {
        value: () => Object.assign({}, defaultConfig),
    },
    closeAll: {
        value: () => document.querySelectorAll('.modal').forEach((modal) => window[modal.getAttribute('id')].close()),
    },
});

export default abmodal;
